/**
 * Слой платформы: всё, что зависит от Telegram, — только здесь.
 * Потом рядом появятся реализации для MAX и Capacitor с тем же интерфейсом.
 */
export interface Platform {
  name: 'telegram' | 'browser';
  /** Заголовок Authorization для API. */
  authHeader(): string;
  /** Параметр запуска (startapp): например, `staff` или `p_<id панели>`. */
  startParam: string | null;
  /** Тёмная ли тема у самого мессенджера. */
  prefersDark(): boolean;
  onThemeChange(cb: () => void): () => void;
  ready(): void;
  haptic(kind: 'light' | 'success' | 'warning' | 'error'): void;
  /** Системная кнопка «Назад» мессенджера; null — скрыть. */
  setBack(handler: (() => void) | null): void;
  confirm(message: string): Promise<boolean>;
  /** Полноэкранный режим (Telegram 8.0+). false — не поддерживается. */
  canFullscreen(): boolean;
  isFullscreen(): boolean;
  setFullscreen(on: boolean): void;
  onFullscreenChange(cb: () => void): () => void;
  /** Цвет шапки и фона мессенджера под наш экран. */
  setChrome(header: string, background: string): void;
  /** Попросить номер телефона: Telegram отправит контакт боту (Bot API 6.9+). false — отказ или не поддерживается. */
  requestContact(): Promise<boolean>;
  /** Не давать Telegram поворачиваться вслед за телефоном (Bot API 8.0). */
  lockOrientation(on: boolean): void;
  canRequestContact(): boolean;
}

interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string };
  colorScheme: 'light' | 'dark';
  version: string;
  isFullscreen?: boolean;
  isVersionAtLeast(v: string): boolean;
  ready(): void;
  expand(): void;
  disableVerticalSwipes?(): void;
  enableVerticalSwipes?(): void;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  setHeaderColor?(c: string): void;
  setBackgroundColor?(c: string): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  requestContact?(cb: (ok: boolean) => void): void;
  lockOrientation?(): void;
  unlockOrientation?(): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium'): void;
    notificationOccurred(type: 'success' | 'warning' | 'error'): void;
  };
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
}

/** Отступы от выреза экрана и кнопок Telegram (полноэкранный режим) → CSS-переменные. */
function applyInsets(tg: TelegramWebApp) {
  const root = document.documentElement.style;
  const set = (prefix: string, v?: Insets) => {
    if (!v) return;
    root.setProperty(`--tg-${prefix}-top`, `${v.top}px`);
    root.setProperty(`--tg-${prefix}-bottom`, `${v.bottom}px`);
  };
  set('safe-area-inset', tg.safeAreaInset);
  set('content-safe-area-inset', tg.contentSafeAreaInset);
}

function telegram(): TelegramWebApp | null {
  const tg = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
  return tg && tg.initData ? tg : null;
}

/** startapp: из ссылки t.me/bot/app?startapp=… или из адреса кнопки бота (…/?startapp=…). */
function urlStartParam(): string | null {
  return new URLSearchParams(location.search).get('startapp');
}

function telegramPlatform(tg: TelegramWebApp): Platform {
  let backHandler: (() => void) | null = null;
  const onBack = () => backHandler?.();
  tg.BackButton.onClick(onBack);
  const v8 = () => tg.isVersionAtLeast('8.0');
  return {
    name: 'telegram',
    authHeader: () => `tma ${tg.initData}`,
    startParam: tg.initDataUnsafe.start_param ?? urlStartParam(),
    prefersDark: () => tg.colorScheme === 'dark',
    onThemeChange(cb) {
      tg.onEvent('themeChanged', cb);
      return () => tg.offEvent('themeChanged', cb);
    },
    ready() {
      tg.ready();
      tg.expand();
      tg.disableVerticalSwipes?.();
      applyInsets(tg);
      for (const ev of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) tg.onEvent(ev, () => applyInsets(tg));
    },
    haptic(kind) {
      if (kind === 'light') tg.HapticFeedback?.impactOccurred('light');
      else tg.HapticFeedback?.notificationOccurred(kind);
    },
    setBack(handler) {
      backHandler = handler;
      if (handler) tg.BackButton.show();
      else tg.BackButton.hide();
    },
    confirm: (message) => new Promise((resolve) => tg.showConfirm(message, resolve)),
    canFullscreen: () => v8() && !!tg.requestFullscreen,
    isFullscreen: () => !!tg.isFullscreen,
    setFullscreen(on) {
      if (!v8()) return;
      if (on) tg.requestFullscreen?.();
      else tg.exitFullscreen?.();
    },
    onFullscreenChange(cb) {
      tg.onEvent('fullscreenChanged', cb);
      return () => tg.offEvent('fullscreenChanged', cb);
    },
    setChrome(header, background) {
      try {
        tg.setHeaderColor?.(header);
        tg.setBackgroundColor?.(background);
      } catch {
        /* старые версии Telegram */
      }
    },
    canRequestContact: () => tg.isVersionAtLeast('6.9') && !!tg.requestContact,
    lockOrientation(on) {
      if (!v8()) return;
      if (on) tg.lockOrientation?.();
      else tg.unlockOrientation?.();
    },
    requestContact: () => new Promise((resolve) => (tg.requestContact ? tg.requestContact(resolve) : resolve(false))),
  };
}

/** Обычный браузер — для разработки: вход заголовком `dev <id>:<имя>` (сервер должен быть с DEV_AUTH=1). */
function browserPlatform(): Platform {
  const params = new URLSearchParams(location.search);
  let devUser = params.get('dev');
  try {
    if (devUser) localStorage.setItem('devUser', devUser);
    else devUser = localStorage.getItem('devUser');
  } catch {
    /* хранилище может быть недоступно */
  }
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  return {
    name: 'browser',
    authHeader: () => {
      // Заголовки HTTP — только латиница, поэтому имя кодируем.
      const [id, name = ''] = (devUser ?? '1001:Администратор').split(':');
      return `dev ${id}:${encodeURIComponent(name)}`;
    },
    startParam: urlStartParam(),
    prefersDark: () => media.matches,
    onThemeChange(cb) {
      media.addEventListener('change', cb);
      return () => media.removeEventListener('change', cb);
    },
    ready() {},
    haptic() {},
    setBack() {},
    confirm: (message) => Promise.resolve(window.confirm(message)),
    canFullscreen: () => !!document.documentElement.requestFullscreen,
    isFullscreen: () => !!document.fullscreenElement,
    setFullscreen(on) {
      if (on) void document.documentElement.requestFullscreen?.().catch(() => {});
      else if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    },
    onFullscreenChange(cb) {
      document.addEventListener('fullscreenchange', cb);
      return () => document.removeEventListener('fullscreenchange', cb);
    },
    setChrome() {},
    canRequestContact: () => false,
    lockOrientation() {},
    requestContact: async () => false,
  };
}

const tg = telegram();
export const platform: Platform = tg ? telegramPlatform(tg) : browserPlatform();
